import {Text} from "@gs-ux-uitoolkit-react/text";
import {Form, FormFeedback, FormControl} from "@gs-ux-uitoolkit-react/form";
import {Input} from "@gs-ux-uitoolkit-react/input";
import {Button} from "@gs-ux-uitoolkit-react/button";
import React, {useState, useEffect, useCallback} from "react";

import {useFormik} from "formik";
import {useSignInContext} from "../context/SignInContextProvider";
import {LoadingIcon} from "@gs-ux-uitoolkit-react/loading";
import { Checkbox } from '@gs-ux-uitoolkit-react/checkbox';
import {useForgotPasswordContext} from "../context/ForgotPasswordContextProvider";

interface FormValue {
    newPassword: string;
    confirmNewPassword: string;
}

export function ResetPasswordForm() {

    const [busy, setBusy] = useState(false);
    const {forgotPasswordAttempt, submitResetPassword} = useForgotPasswordContext();
    const {checkInIAMHub} = useSignInContext();
    const [iamHubStatus, setIamHubStatus] = useState<boolean | null>(null);

    useEffect(() => {
        (async () => {
            try {
                // @ts-ignore
                const result = await checkInIAMHub(forgotPasswordAttempt?.loginId);
                const isEnabled = result.data.isUserMigratedStatus === 2 && result.data.enableIamHubMigrationFlow === true;
                setIamHubStatus(isEnabled);
            } catch (e) {
                setIamHubStatus(false);
            }
        })();
    }, [checkInIAMHub, forgotPasswordAttempt]);

    const initialValues = {
        newPassword: '',
        confirmNewPassword: '',
    };

    const disabled = busy ;

    const formik = useFormik<FormValue>({
        initialValues,
        onSubmit: async (values, {setValues}) => {
            setBusy(true);
            try {
                //@ts-ignore
                await submitResetPassword(forgotPasswordAttempt.loginId, values.newPassword, values.confirmNewPassword);
                setValues(initialValues);
            } finally {
                setBusy(false);
            }
        }
    });

    return (
        <div className="flex flex-col items-stretch gap-6 w-full">
            <Text typography="textTitle02" data-qa-comp="reset-password-title">Create a new password</Text>
            <Text typography="textBody02Medium" color="colorTextNeutralBold">
                {iamHubStatus ? (
                    <>
                        Your password must be 10–64 characters long and must contain at least one upper-case letter, one lower-case letter, one number, and one symbol.
                        <br />
                        <span style={{ display: 'inline-block', margin: '12px 0' }} />
                        Your password must not contain more than three consecutive identical or sequential characters.
                    </>
                ) : (
                    <>Your password must be 10–64 characters long and must contain at least one upper-case letter, one lower-case letter, one number, and one symbol.</>
                )}
            </Text>
            <Form className="flex flex-col gap-4 w-full" preventSubmit>
                <FormControl status={formik.touched.newPassword && formik.errors.newPassword ? 'error' : 'none'}>
                    <Input
                        autoFocus
                        name="newPassword"
                        type="password"
                        size="md"
                        emphasis="regular"
                        label="New password"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.newPassword}
                        data-qa-comp="new-password-input"
                    />
                    <FormFeedback>{formik.touched.newPassword ? formik.errors.newPassword : null}</FormFeedback>
                </FormControl>
                <FormControl status={formik.touched.confirmNewPassword && formik.errors.confirmNewPassword ? 'error' : 'none'}>
                    <Input
                        name="confirmNewPassword"
                        type="password"
                        size="md"
                        emphasis="regular"
                        label="Confirm new password"
                        className="w-full"
                        disabled={disabled}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.confirmNewPassword}
                        data-qa-comp="confirm-new-password-input"
                    />
                    <FormFeedback>{formik.touched.confirmNewPassword ? formik.errors.confirmNewPassword : null}</FormFeedback>
                </FormControl>
                <Button
                    className="w-full"
                    size="md"
                    appearance="highlight"
                    emphasis="bold"
                    type="submit"
                    onClick={formik.submitForm}
                    disabled={disabled}
                    data-qa-comp="continue-button"
                >
                    {!busy ? 'Continue' : <LoadingIcon size="sm"/>}
                </Button>
            </Form>
        </div>
    );
}